import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import {
  getLocalStorageExpire,
  setLocalStorageExpire,
} from '@/libs/utils/cookie-tool';
import axios from '../api/ai-axios';
import { setUID, setUserData, setAccountType } from '@/reducers/users';
import { toggleLoginModal } from '@/reducers/login-pop';
import useSXNU from './useSXNU';

/**
 * 权限管理 Hook - 合并社区版和教育版认证逻辑
 */
export const useAuth = () => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSXNU } = useSXNU();

  const uid = useSelector((state) => state.users.uid);
  const userData = useSelector((state) => state.users.data);

  const expiresTime = 60 * 24 * 30; // cookie过期时间, 单位：分钟

  /**
   * 检查登录状态 - 合并两种认证方式
   */
  const checkLogin = async () => {
    setLoading(true);
    try {
      await checkWebLogin();
    } catch (err) {
      message.warning(err.message);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 登录检查
   */
  const checkWebLogin = async () => {
    let { search, pathname } = window.location;
    let { VITE_ORIGIN } = import.meta.env;
    let params = new window.URLSearchParams(search);
    localStorage.removeItem('userData');

    // 检查是否有jwt或uuid参数，如果有则进行相应登录
    if (params.has('jwt') || params.has('uuid')) {
      const jwt = params.get('jwt');
      const uuid = params.get('uuid');

      // 清理URL参数
      params.delete('jwt');
      params.delete('uuid');
      let paramsString = params.toString();
      let newUrl = `${VITE_ORIGIN}${pathname}${paramsString ? `?${paramsString}` : ''}`;
      window.history.replaceState({}, '', newUrl);

      // 根据参数类型选择不同的登录接口
      let res;
      if (jwt) {
        // JWT SSO登录
        res = await axios.post(`/api/login_sso?jwt=${jwt}`);
      } else {
        // UUID登录
        res = await axios.post('/api/login', { uuid });
      }

      if (res.status === 1) {
        setLocalStorageExpire('uid', res.data.uid, expiresTime);
        dispatch(setUID(res.data.uid));
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('uid');
        setIsAuthenticated(false);
        throw new Error(res.message);
      }
    } else {
      // 检查本地存储的 uid
      let userUid = getLocalStorageExpire('uid');

      if (userUid) {
        dispatch(setUID(userUid));
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        return;
      }
    }

    // 获取用户信息
    let userData = await axios.post('/api/user/getUserInfo', {
      secret: 'zmrobo',
    });

    if (userData.status === 1) {
      dispatch(setUserData(userData.data));
      const currentAccountType =
        userData.data.accountType === '校园账号' ? 'zhongming' : 'student';
      dispatch(setAccountType(currentAccountType));

      setIsAuthenticated(true);
    } else {
      localStorage.removeItem('uid');
      dispatch(setUID(''));
      dispatch(setUserData(null));
      setIsAuthenticated(false);
      dispatch(toggleLoginModal(true));
      throw new Error(userData.message);
    }
  };

  /**
   * 检查路由权限 - 合并两种权限检查逻辑
   */
  const checkRoutePermission = () => {
    if (!uid || uid === '') {
      navigate(isSXNU ? '/sxnu' : '/');
      return false;
    }

    // VIP 权限检查（针对特定路由）
    if (
      location.pathname.startsWith('/lesson') &&
      userData?.authInfo?.vip !== 2
    ) {
      navigate('/ailab');
      return false;
    }

    return true;
  };

  /**
   * 检查VIP权限
   */
  const hasVipPermission = (requiredLevel) => {
    return userData?.authInfo?.vip === requiredLevel;
  };

  /**
   * 登出
   */
  const logout = () => {
    localStorage.removeItem('uid');
    localStorage.removeItem('userData');
    dispatch(setUID(''));
    dispatch(setUserData(null));
    setIsAuthenticated(false);
    navigate(isSXNU ? '/sxnu' : '/');
  };

  return {
    loading,
    isAuthenticated,
    uid,
    userData,
    checkLogin,
    checkRoutePermission,
    hasVipPermission,
    logout,
  };
};

export default useAuth;
